/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import MarkerClusterer, { MarkerClustererOptions } from '@googlemaps/markerclustererplus';
import { Cluster } from '@googlemaps/markerclustererplus/dist/cluster';
import { BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, switchMapTo, take, takeUntil, tap } from 'rxjs/operators';

import { isArrayNullOrEmpty, isElementNull, isStringNullOrEmpty } from '../../../../utils';
import {
  EAllPlotTypes,
  EMapTypeId,
  EOrientation,
  EPlotIcon,
  EPlotType,
  EPolylineHoverTrigger,
  IBalloon,
  IClusterBalloon,
  IClusterClickEvent,
  ICustomClickEventData,
  ILatLng,
  IMapContextMenuData,
  IMaxPlottableMarkers,
  IPlotTypeDependency,
  IPoint,
  IPolylineHoverParams,
  IPolylineInfo,
  LatLng,
  MarkerInfo,
  MarkerLabel,
  Position
} from '../../../core/models';
import { MapIconRegistryService, WindowResizeService } from '../../../core/services';
import { defaultLabelInfoBoxOptions } from '../../containers/map/map-plot-configs.constants';
import { IBalloonInfo, IClusterer, ICollectionDiff, ILabelInfo, InfoBoxOptions, IPlotConfig, ISize, MapDiffer, Marker } from '../../models';
import { GoogleMapsLoaderService } from '../../services';
import { BalloonComponent } from '../balloon/balloon.component';
import { LabelComponent } from '../label/label.component';
import { saturatedMapStyle } from './google-maps-saturated-style';

@Component({
  selector: 'fm-google-maps',
  templateUrl: './google-maps.component.html',
  styleUrls: ['./google-maps.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleMapsComponent implements OnDestroy, OnInit, OnChanges, AfterViewInit {
  @Input()
  public balloonTemplate: TemplateRef<any>;

  @Input()
  public zoomToMarker: ILatLng;

  @Input()
  public selectedMarker: MarkerInfo;

  @Input()
  public set balloon(value: IBalloon) {
    this.mapReady$.subscribe(() => {
      if (isElementNull(value)) {
        this._closeBalloon();
      } else {
        this._showBalloon(value);
      }
    });
  }

  @Input()
  public set markers(markers: MarkerInfo[]) {
    // markers are updated via google maps library so it doesn't need to run through
    // Angular zone
    this._zone.runOutsideAngular(() => {
      this._markersToProcessSubject.next(markers);
    });
  }

  @Input()
  public set polylines(lines: IPolylineInfo[]) {
    if (isElementNull(lines)) {
      return;
    }

    // polylines are updated via google maps library so it doesn't need to run through
    // Angular zone
    this._zone.runOutsideAngular(() => {
      this.mapReady$.subscribe(() => this._setPolylines(lines));
    });
  }

  @Input()
  public set isStreetViewVisible(isVisible: boolean) {
    this._googleMapInitialized$.subscribe(() => {
      if (!isVisible) {
        this.closeStreetView();
      }
    });
  }

  @Input()
  public set overlayControlMargins(position: Position) {
    if (!isElementNull(this._overlayMargins) && Position.equals(position, this._overlayMargins)) {
      return;
    }
    this._overlayMargins = position;
    this._updateMapOffset();
    this._mapProjected$.subscribe(() => this._checkAndExecuteFitToMap());
  }

  @Input()
  public set mapType(mapType: EMapTypeId) {
    this._mapType = mapType;
    this._googleMapInitialized$.subscribe(() => this._setMapType());
  }

  @Input()
  public set isTrafficEnabled(isTrafficEnabled: boolean) {
    this._isTrafficEnabled = isTrafficEnabled;
    this._googleMapInitialized$.subscribe(() => this._toggleTrafficLayer());
  }

  @Input()
  public set streetViewToLocation(location: LatLng) {
    this._googleMapInitialized$.subscribe(() => {
      if (location) {
        this._setPanoramaLocation(location);
      } else {
        this.closeStreetView();
      }
    });
  }

  @Input()
  public set plotClusterConfig(plotClusterConfig: Map<MarkerClustererOptions, EPlotType[]>) {
    this._plotClusterConfig = plotClusterConfig;
    this._markerClustererLoaded$.subscribe(() => {
      this._setupMarkerClusterers();
      this._toggleClustering();
    });
  }

  @Input()
  public set zoomToLocation(location: ILatLng) {
    if (!isElementNull(location)) {
      this._mapStable$.subscribe(() => this._setZoomToLocation(location));
    }
  }

  @Input()
  public set plotTypesToShowLabel(plotTypesToShowLabel: EPlotType[]) {
    this._plotTypesToShowLabel = plotTypesToShowLabel;
    this.mapReady$.subscribe(() => {
      this._getPlotsInBoundsAndEmitIfChanged(this.map.getBounds());

      if (isArrayNullOrEmpty(this._plotTypesToShowLabel)) {
        this._hideLabels();
      } else {
        this._showAllLabels();
      }
    });
  }

  @Input()
  public set plotTypeConfigs(plotTypeConfigs: IPlotConfig[]) {
    this._plotTypeConfigs = plotTypeConfigs;
    plotTypeConfigs.forEach(config => {
      if (config.zIndex) {
        if (config.zIndex instanceof Map) {
          config.zIndex.forEach(zIndex => {
            if (zIndex > this._maxMarkerZIndex) {
              this._maxMarkerZIndex = zIndex;
            }
          });
        } else if (this._maxMarkerZIndex < config.zIndex) {
          this._maxMarkerZIndex = config.zIndex;
        }
      }
    });
  }

  @Input()
  public set plotTypeDependencies(plotTypeDependencies: IPlotTypeDependency[]) {
    this._plotTypeDependencies = plotTypeDependencies;
  }

  @Input()
  public set maxPlottableMarkers(value: IMaxPlottableMarkers[]) {
    if (!isElementNull(value)) {
      this._maxPlottableMarkers = value;
      this._maxPlottableMarkersInitializedSubject.next(true);
    }
  }

  @Input()
  public set markersLabels(markersLabels: MarkerLabel[]) {
    this._markersLabels = markersLabels;

    this.mapReady$.subscribe(() => {
      if (!isArrayNullOrEmpty(this._markersLabels) && !isArrayNullOrEmpty(this._plotTypesToShowLabel)) {
        this._showAllLabels();
      } else {
        this._hideLabels();
      }
    });
  }

  @Input()
  public set markerLabel(markerLabel: MarkerLabel) {
    this.mapReady$.subscribe(() => {
      if (isArrayNullOrEmpty(this._plotTypesToShowLabel)) {
        this._hideLabels();
      } else {
        this._hideLabels(this._plotTypesToShowLabel);
      }

      if (!isElementNull(markerLabel)) {
        const markerToShowLabel = this._findMarkerById(markerLabel.id);
        if (!isElementNull(markerToShowLabel)) {
          this._showLabel(markerLabel, markerToShowLabel);
        }
      }
    });
  }

  @Input()
  public set startLocation(location: LatLng[]) {
    if (isArrayNullOrEmpty(location) || !isElementNull(this.map)) {
      return;
    }

    this._googleMapsLoaderService.googleMapsLoaded$.pipe(filter(isLoaded => isLoaded && isElementNull(this.map))).subscribe(() => {
      const { center, zoom }: { center: google.maps.LatLng; zoom: number } = this._getCenterAndZoomForMapInit(location);
      this._mapCenter = new LatLng(center.lat(), center.lng());

      this._init(center, zoom);
    });
  }

  @Input()
  public shouldFitToMap: boolean;

  @Input()
  public set triggerFitToMap(trigger: Object) {
    if (trigger) {
      this.fitToMap();
    }
  }

  @Input()
  public set markerToHighlight(marker: MarkerInfo) {
    this._positionHighlightOverlayInitialized$.pipe(take(1)).subscribe(() => this._handleMarkerHighlight(marker));
  }

  @Input()
  public set changeBounds(latLngs: ILatLng[]) {
    if (isElementNull(latLngs)) {
      return;
    }
    this._mapStable$.subscribe(() => this._updateBounds(latLngs));
  }

  @Input()
  public set clusterBalloonZoomToClicked(clusterBalloon: IClusterBalloon) {
    if (clusterBalloon) {
      const cluster = this._getClusterForMarker({
        id: clusterBalloon.clusteredItems[0].id,
        type: clusterBalloon.clusteredItems[0].type
      } as MarkerInfo);
      this._clusterZoomTo(cluster);
    }
  }

  @Output()
  public boundsChanged = new EventEmitter<google.maps.LatLngBounds>();
  @Output()
  public currentPlotChanged = new EventEmitter<MarkerInfo>();
  @Output()
  public fitToMapUpdated = new EventEmitter<boolean>();
  @Output()
  public mapCenterChanged = new EventEmitter<LatLng>();
  @Output()
  public markerRightClicked = new EventEmitter<IMapContextMenuData<MarkerInfo>>();
  @Output()
  public markerClicked = new EventEmitter<ICustomClickEventData<any>>();
  @Output()
  public plotsInBoundsChanged = new EventEmitter<MarkerInfo[]>();
  @Output()
  public rightClicked = new EventEmitter<IMapContextMenuData<null>>();
  @Output()
  public streetViewChanged = new EventEmitter<boolean>();
  @Output()
  public balloonClosed = new EventEmitter<void>();
  @Output()
  public removeZoomToMarker = new EventEmitter<void>();
  @Output()
  public polylineStartHover = new EventEmitter<IPolylineHoverParams>();
  @Output()
  public polylineEndHover = new EventEmitter<void>();
  @Output()
  public polylineClicked = new EventEmitter<IPolylineInfo>();
  @Output()
  public zoomLevelChanged = new EventEmitter<number>();
  @Output()
  public mapTilesLoadedFirstTime = new EventEmitter<void>();
  @Output()
  public currentPlotStartHover = new EventEmitter<MarkerInfo>();
  @Output()
  public currentPlotEndHover = new EventEmitter<void>();
  @Output()
  public clusterClicked = new EventEmitter<IClusterClickEvent>();
  @Output()
  public mapLoaded = new EventEmitter<void>();

  @HostBinding('class.maptype-roadmap')
  get roadmapMaptype(): boolean {
    return this._mapType === EMapTypeId.ROADMAP;
  }

  @HostBinding('class.maptype-hybrid')
  get hybridMaptype(): boolean {
    return this._mapType === EMapTypeId.HYBRID;
  }

  @HostBinding('class.maptype-saturated')
  get satelliteMaptype(): boolean {
    return this._mapType === EMapTypeId.SATURATED;
  }

  // show the map after it's loaded to avoid flickering when map center is updated
  public showMap = false;
  public map: google.maps.Map;
  public mapReady$: Observable<void>;

  @ViewChild('mapHostElement', { read: ElementRef })
  private readonly _mapHostElement: ElementRef;
  private _mapHostOffsetTop: number;

  private readonly _defaultBalloonInfoBoxOptions = {
    boxClass: 'balloon',
    closeBoxURL: '',
    disableAutoPan: true,
    enableEventPropagation: false,
    zIndex: 20
  };

  private _mapCenter: LatLng;
  // inner map: markers by id
  // outer map: markersById by plot type
  private readonly _markersByType: Map<EAllPlotTypes, Map<number, Marker>> = new Map<EAllPlotTypes, Map<number, Marker>>();
  private readonly _markersDifferByType = new Map<EPlotType, MapDiffer<MarkerInfo, number>>();
  private readonly _plotsInBoundsDifferByType = new Map<EAllPlotTypes, MapDiffer<MarkerInfo, number>>();
  private _mapBounds: google.maps.LatLngBounds;
  private _markerShape: google.maps.Polygon | google.maps.Circle;
  private _mapOffset: Position;
  private readonly _mapDimensions: ISize = { height: 0, width: 0 };
  private _overlayMargins: Position;
  private _trafficLayer: google.maps.TrafficLayer;
  private _isTrafficEnabled: boolean;
  private _zoomToLocationMarker: CustomMarker;
  private _mapType: EMapTypeId;
  private readonly _streetViewZoom = 1;
  private readonly _zoomLabelPlotId = 0;
  private readonly _zoomPlotId = 'zoomPlotId';
  private _plotClusterConfig: Map<MarkerClustererOptions, EPlotType[]> = new Map<MarkerClustererOptions, EPlotType[]>();
  private readonly _clusterersMap: Map<EPlotType, IClusterer> = new Map<EPlotType, IClusterer>();
  private _plotTypesToShowLabel: EAllPlotTypes[] = [];
  private readonly _labelInfo: ILabelInfo[] = [];
  private _markersLabels: MarkerLabel[] = [];
  private _plotTypeConfigs: IPlotConfig[] = [];
  private _plotTypeDependencies: IPlotTypeDependency[] = [];
  private _balloon: IBalloonInfo;
  private _clusterClickTime: Date;

  // highlight that is "on hover", when mouse hovers on a plot or on the status panel
  private _positionHighlightOverlay: {
    highlight: PositionHighlightOverlay;
    marker: MarkerInfo;
  } = null;

  // highlight that is "on click" - added once a plot is selected
  private _selectedMarkerPositionHighlightOverlay: {
    highlight: PositionHighlightOverlay;
    marker: MarkerInfo;
  } = null;

  private readonly _polylines = new Map<
    number,
    {
      info: IPolylineInfo;
      options: google.maps.PolylineOptions;
      line: google.maps.Polyline;
    }
  >();
  private readonly lineSymbol: google.maps.Symbol = {
    path: 'M 0,-1 0,1',
    strokeColor: '#0088CE',
    scale: 4
  };

  private readonly polylineBaseOptions: google.maps.PolylineOptions = {
    geodesic: true,
    strokeColor: '#005E8F',
    strokeWeight: 6,
    zIndex: 10,
    icons: [
      {
        icon: this.lineSymbol,
        repeat: '4px'
      }
    ]
  };

  private readonly _defaultZoom = 18;
  private readonly _maxZoomLevelForFitToMap = 16;
  private readonly _clusterZoomToMaxZoomLevel = 22;

  private _isAddingZoomToMarker: boolean;
  private _maxMarkerZIndex = 0;
  private readonly _defaultZIndex = 0;

  private _isUpdatingMapCenter: boolean;
  private _maxPlottableMarkers: IMaxPlottableMarkers[];
  private readonly _googleMapInitializedSubject = new BehaviorSubject<boolean>(false);
  private _googleMapInitialized$: Observable<void>;
  private readonly _boundsChangedSubject = new BehaviorSubject<boolean>(false);
  private _boundsChanged$: Observable<void>;
  private readonly _mapProjectedSubject = new BehaviorSubject<boolean>(false);
  private _mapProjected$: Observable<void>;
  private readonly _maxPlottableMarkersInitializedSubject = new BehaviorSubject<boolean>(false);
  private _maxPlottableMarkersInitialized$: Observable<void>;
  private readonly _clusteringInitializedSubject = new BehaviorSubject<boolean>(false);
  private _clusteringInitialized$: Observable<void>;
  private _positionHighlightOverlayInitialized$: Observable<void>;
  private readonly _mapStableSubject = new BehaviorSubject<boolean>(false);
  private _mapStable$: Observable<void>;
  private _mapStable: boolean;
  private _markerClustererLoaded$: Observable<void>;
  private readonly _isProcessingMarkersSubject = new BehaviorSubject<boolean>(false);
  private _readyToProcessMarkers$: Observable<void>;
  private readonly _markersToProcessSubject = new BehaviorSubject<MarkerInfo[]>([]);
  private readonly _markersToProcess$ = this._markersToProcessSubject.asObservable();

  private readonly _unsubscribe$ = new Subject<void>();
  private removeIndexLoaderListener: google.maps.MapsEventListener;

  constructor(
    private readonly _googleMapsLoaderService: GoogleMapsLoaderService,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _zone: NgZone,
    private readonly _mapIconRegistryService: MapIconRegistryService,
    private readonly _windowResizeService: WindowResizeService
  ) {
    this._setupInitializationNotifiers();
  }

  public ngAfterViewInit(): void {
    // initialize map dimensions
    this._updateMapDimensions();

    // update map dimensions on window resize
    this._windowResizeService.windowSize$.pipe(takeUntil(this._unsubscribe$), debounceTime(100)).subscribe(() => {
      this._updateMapDimensions();
      this._checkAndExecuteFitToMap();
    });
  }

  public ngOnInit(): void {
    this._processMarkersWhenReadyToProcess();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // explict call 'zoomToMarker' change before 'selectedMarker' so have correct zoom level
    if (changes.zoomToMarker) {
      this.setZoomToMarker(this.zoomToMarker);
    }
    if (changes.selectedMarker) {
      this.setSelectedMarker(this.selectedMarker);
    }
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public fitToMap(): void {
    if (isElementNull(this._overlayMargins) || this._getMarkersSize() === 0) {
      return;
    }

    const markersCanBeFitted = this._getMarkersOfType(
      this._plotTypeConfigs.filter(typeConfig => typeConfig.canBeFittedToMap).map(typeConfig => typeConfig.type)
    );

    if (markersCanBeFitted.length === 0) {
      return;
    }
    this._updateBounds(markersCanBeFitted.map(m => m.markerInfo.latLng));

    this.fitToMapUpdated.emit(true);

    if (this._zoomToLocationMarker) {
      this._zoomToLocationMarker.setMap(undefined);
      this._hideLabel(this._zoomLabelPlotId);
    }
  }

  public closeStreetView(): void {
    const panorama = this.map.getStreetView();
    if (panorama) {
      panorama.setVisible(false);
    }
  }

  private _setupInitializationNotifiers(): void {
    this._googleMapInitialized$ = this._googleMapInitializedSubject.asObservable().pipe(
      filter(isInitialized => isInitialized),
      take(1),
      map(() => void 0)
    );
    this._boundsChanged$ = this._boundsChangedSubject.asObservable().pipe(
      filter(isChanged => isChanged),
      take(1),
      map(() => void 0)
    );
    this._mapProjected$ = this._mapProjectedSubject.asObservable().pipe(
      filter(isProjected => isProjected),
      take(1),
      map(() => void 0)
    );
    this._maxPlottableMarkersInitialized$ = this._maxPlottableMarkersInitializedSubject.asObservable().pipe(
      filter(isInitialized => isInitialized),
      take(1),
      map(() => void 0)
    );
    this._clusteringInitialized$ = this._clusteringInitializedSubject.asObservable().pipe(
      filter(isInitialized => isInitialized),
      take(1),
      map(() => void 0)
    );
    this._positionHighlightOverlayInitialized$ = combineLatest([
      this._googleMapInitialized$,
      this._googleMapsLoaderService.positionHighlightOverlayLoaded$
    ]).pipe(
      filter(([, positionHighlightOverlayLoaded]) => positionHighlightOverlayLoaded),
      take(1),
      map(() => void 0)
    );

    this._markerClustererLoaded$ = this._boundsChanged$.pipe(
      take(1),
      map(() => void 0)
    );

    this.mapReady$ = combineLatest([
      this._boundsChanged$,
      this._mapProjected$,
      this._maxPlottableMarkersInitialized$,
      this._clusteringInitialized$,
      this._googleMapsLoaderService.customMarkerLoaded$,
      this._googleMapsLoaderService.infoBoxLoaded$
    ]).pipe(
      filter(([, , , , customerMarkerLoaded, infoBoxLoaded]) => customerMarkerLoaded && infoBoxLoaded),
      take(1),
      map(() => void 0)
    );

    this._mapStable$ = this._mapStableSubject.asObservable().pipe(
      filter(isMapStable => isMapStable),
      take(1),
      map(() => void 0)
    );

    this._readyToProcessMarkers$ = this._isProcessingMarkersSubject.asObservable().pipe(
      filter(isProcessing => !isProcessing),
      map(() => void 0)
    );
  }

  private _init(center: google.maps.LatLng, zoomLevel: number): void {
    this._initMap(center, zoomLevel);
    this._initStreetView();
    this._initMapListeners();

    (<IFleetmaticsWindow>(<unknown>window)).Fleetmatics = {
      Map: {
        Instance: this.map
      }
    };

    this._updateCenter();
    this._googleMapInitializedSubject.next(true);
  }

  private _initMap(center: google.maps.LatLng, zoomLevel: number): void {
    this.map = new google.maps.Map(this._mapHostElement.nativeElement, {
      backgroundColor: '#FFFFFF',
      center,
      clickableIcons: false,
      disableDefaultUI: true,
      streetViewControl: true,
      zoom: zoomLevel,
      minZoom: 3,
      zoomControl: true,
      scaleControl: true
    });
  }

  private _initStreetView(): void {
    this.map.getStreetView().setOptions({
      enableCloseButton: false,
      fullscreenControl: false,
      addressControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER
      }
    });
  }

  private _initMapListeners(): void {
    this._initDragMapListeners();

    this._initMapIdleListener();

    this._initMapZoomChangedListenter();

    this._initStreetViewChangedListener();

    this._initContextMenuEventListener();

    this._initMapOneTimeEventListeners();
  }

  private _initStreetViewChangedListener(): void {
    google.maps.event.addListener(this.map.getStreetView(), 'visible_changed', () => {
      this._zone.run(() => {
        const isVisible = this.map.getStreetView().getVisible();
        this.streetViewChanged.emit(isVisible);
      });
    });
  }

  private _initMapZoomChangedListenter(): void {
    this.map.addListener('center_changed', () => {
      this._zone.run(() => {
        if (!this._mapStable) {
          return;
        }

        this._disableFitToMap();
        this._removeZoomToMarker();
        this._updateCenter();
      });
    });
    this.map.addListener('zoom_changed', () => {
      this._zone.run(() => {
        if (!this._mapStable) {
          return;
        }

        this._disableFitToMap();
        this._allowMarkersToDraw();
        this.zoomLevelChanged.emit(this.map.getZoom());

        // clusters disappear on zoom change so we need to:
        this._onClusterRemoved();
      });
    });
  }

  private _disableFitToMap(): void {
    this.fitToMapUpdated.emit(false);
  }

  private _removeZoomToMarker(): void {
    if (this._isAddingZoomToMarker) {
      return;
    }

    this.removeZoomToMarker.emit();
  }

  private _initMapIdleListener(): void {
    this.removeIndexLoaderListener = this.map.addListener('idle', () => {
      this.mapLoaded.emit();
      this.removeIndexLoaderListener.remove();
    });

    this.map.addListener('idle', () => {
      this._zone.run(() => {
        if (!this.showMap) {
          this.showMap = true;
        }

        this._emitMapBounds();
        if (!this._mapStable) {
          this._mapStable = true;
          this._mapStableSubject.next(true);
        }
      });
    });
  }

  private _initDragMapListeners(): void {
    this.map.addListener('dragstart', () => {
      this._zone.run(() => {
        this._preventMarkersFromDrawing();
      });
    });

    this.map.addListener('dragend', () => {
      this._zone.run(() => {
        this._allowMarkersToDraw();
      });
    });
  }

  private _initMapOneTimeEventListeners(): void {
    google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
      this._zone.run(() => {
        this.mapTilesLoadedFirstTime.emit();
        this._googleMapsLoaderService.loadUtilityLibraries();
      });
    });

    google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
      this._zone.run(() => {
        /*  when map is initialized, bounds cannot take into account the overlay margins because the projection of
            the map is unknown. _setOffsetCenter() will offset the map slightly - this happens before the first idle event
            so the map is not yet displayed to the user. note this evenlistener only runs once
        */
        this._setOffsetCenter(this.map.getCenter());
        this._boundsChangedSubject.next(true);
      });
    });

    google.maps.event.addListenerOnce(this.map, 'projection_changed', () => {
      this._zone.run(() => {
        this._mapProjectedSubject.next(true);
        if (this._getMarkersSize() > 0) {
          setTimeout(() => {
            this._checkAndExecuteFitToMap();
          });
        }
      });
    });
  }

  private _initContextMenuEventListener(): void {
    this._mapHostElement.nativeElement.addEventListener('contextmenu', (event: MouseEvent) => {
      if (this.map.getStreetView().getVisible()) {
        return;
      }
      setTimeout(() => {
        const overlay = new google.maps.OverlayView();
        overlay.onAdd = () => {
          const projection = overlay.getProjection();
          const streetViewService = new google.maps.StreetViewService();
          const point: IPoint = {
            x: event.clientX,
            y: event.clientY - this._mapHostOffsetTop
          };
          const pixel = new google.maps.Point(point.x, point.y);
          const rightClickLatLng = projection.fromContainerPixelToLatLng(pixel);
          streetViewService.getPanoramaByLocation(rightClickLatLng, 50, (data: any, status: any) => {
            const clickLatLng = new LatLng(rightClickLatLng.lat(), rightClickLatLng.lng());
            const menuData: IMapContextMenuData<null> = {
              clickPosition: point,
              clickLatLng,
              streetViewAvailable: false
            };
            this._zone.run(() => {
              menuData.streetViewAvailable = status === google.maps.StreetViewStatus.OK;

              this.rightClicked.emit(menuData);
            });
          });
        };
        overlay.setMap(this.map);
      });
    });
  }

  private _preventMarkersFromDrawing(): void {
    this._runForAllMarkers(marker => {
      // prevent draw method from running
      marker.googleMarker.setCanDraw(false);
    });
  }

  private _allowMarkersToDraw(): void {
    this._runForAllMarkers(marker => {
      // allow markers to be drawn
      marker.googleMarker.setCanDraw(true);
    });
  }

  private _runForAllMarkers(func: (marker: Marker) => void): void {
    this._markersByType.forEach(markersMap => {
      markersMap.forEach(markerToRun => {
        func(markerToRun);
      });
    });
  }

  private _setupMarkerClusterers() {
    this._plotClusterConfig.forEach((plotTypes, clusterOptions) => {
      const plotTypesWhichAreNotAlreadySet = plotTypes.filter(plotType => isElementNull(this._clusterersMap.get(plotType)));
      if (plotTypesWhichAreNotAlreadySet.length === 0) {
        // All plots to be grouped already have a clusterer associated, quit
        return;
      }

      const newClusterer = new MarkerClusterer(this.map, [], clusterOptions);
      plotTypes.forEach(plotType => {
        this._clusterersMap.set(plotType, newClusterer);
      });

      google.maps.event.addListener(newClusterer, 'clusteringend', () => {
        if (this._labelInfo.length > 0 || !isElementNull(this._balloon)) {
          this._zone.run(() => {
            plotTypes.forEach(plotType => {
              this._clearComponentsFromClusteredMarkers(plotType);
            });
          });
        }
      });

      if (clusterOptions.zoomOnClick === false) {
        this._setupClustererEvents(newClusterer);
      }
    });

    this._clusteringInitializedSubject.next(true);
  }

  private _setupClustererEvents(newClusterer: MarkerClusterer) {
    this._setIdsToClusters(newClusterer);
    this._setupClusterClick(newClusterer);
    this._setupClusterHover(newClusterer);
  }

  private _setupClusterHover(newClusterer: MarkerClusterer): void {
    google.maps.event.addListener(newClusterer, 'mouseover', (cluster: Cluster) => {
      const clusterMarkerInfo: MarkerInfo = this._clusterToMarkerInfo(cluster);
      this._handleMarkerHighlight(clusterMarkerInfo);
    });

    google.maps.event.addListener(newClusterer, 'mouseout', () => {
      this._removeHighlight();
    });
  }

  private _setupClusterClick(newClusterer: MarkerClusterer): void {
    google.maps.event.addListener(newClusterer, 'click', (cluster: Cluster) => {
      // Manually handle double click as there is no dblclick event on clusters
      if (this._isClusterDoubleClick()) {
        this._clusterZoomTo(cluster);
      } else {
        this._handleClusterSingleClick(cluster);
      }
    });
  }

  private _setIdsToClusters(newClusterer: MarkerClusterer) {
    google.maps.event.addListener(newClusterer, 'clusteringend', () => {
      // Assign ids to the clusters
      newClusterer.getClusters().forEach((cluster: any, index: number) => {
        cluster.clusterIcon_.set('id', index);
      });
    });
  }

  private _highlightSelectedCluster(cluster: Cluster): void {
    const clusterMarker = this._clusterToMarkerInfo(cluster);
    const isClusterHighlighted =
      this._selectedMarkerPositionHighlightOverlay?.marker?.type === clusterMarker.type &&
      this._selectedMarkerPositionHighlightOverlay?.marker?.id === clusterMarker.id;
    // highlight cluster as selected, or remove highlight if already selected
    this._handleSelectedMarkerHighlight(isClusterHighlighted ? null : clusterMarker);
  }

  private _clusterToMarkerInfo(cluster: Cluster): MarkerInfo {
    const clusterLatLng = cluster.getMarkers()[0].getPosition();
    return {
      id: (cluster as any).clusterIcon_.get('id'),
      latLng: new LatLng(clusterLatLng.lat(), clusterLatLng.lng()),
      options: { iconName: EPlotIcon.Cluster },
      title: null,
      type: EPlotType.cluster
    };
  }

  private _emitMapBounds(): void {
    const newBounds = this.map.getBounds();
    if (!!newBounds) {
      // initialize member for the first time
      if (isElementNull(this._mapBounds)) {
        this._mapBounds = newBounds;
        this.boundsChanged.emit(newBounds);
        return;
      }

      if (!this._mapBounds.equals(newBounds)) {
        this._mapBounds = newBounds;
        this.boundsChanged.emit(newBounds);

        this._getPlotsInBoundsAndEmitIfChanged(newBounds);
      }
    }
  }

  private _getPlotsInBoundsAndEmitIfChanged(bounds: google.maps.LatLngBounds): void {
    if (isArrayNullOrEmpty(this._plotTypesToShowLabel)) {
      return;
    }

    let plotsInBounds: MarkerInfo[] = [];
    let plotsInBoundsAddedOrRemoved = false;
    this._plotTypesToShowLabel.forEach(plotType => {
      const plostInBoundsPerType = this._getMarkersOfType([plotType])
        .filter(marker => bounds.contains(marker.googleMarker.getPosition()))
        .map(m => m.markerInfo);

      plotsInBounds = plotsInBounds.concat(plostInBoundsPerType);

      const plotsInBoundsDiff = this._getPlotsInBoundDiff(plotType, plostInBoundsPerType);

      if (plotsInBoundsDiff.added.length > 0 || plotsInBoundsDiff.removed.length > 0) {
        plotsInBoundsAddedOrRemoved = true;
      }
    });

    if (plotsInBoundsAddedOrRemoved) {
      this.plotsInBoundsChanged.emit(plotsInBounds);
    }
  }

  private _setPolylines(linesInfo: IPolylineInfo[]): void {
    // empty array
    if (linesInfo.length === 0) {
      // should we remove polylines?
      if (this._polylines.size > 0) {
        this._polylines.forEach(polyline => {
          polyline.line.setMap(null);
        });
        this._polylines.clear();
      }

      return;
    }

    linesInfo.forEach(lineInfo => {
      if (this._polylines.has(lineInfo.id)) {
        this._updatePolyline(lineInfo);
      } else {
        this._createPolyline(lineInfo);
      }
    });

    // Workaround to force polyline to redraw when zooming in/out
    this.map.setZoom(this.map.getZoom());
  }
  private _updatePolyline(newPolylineInfo: IPolylineInfo): void {
    const polylineObject = this._polylines.get(newPolylineInfo.id);

    if (newPolylineInfo.isActive !== polylineObject.info.isActive) {
      const newOptions = this._getPolylineOptions(newPolylineInfo);
      polylineObject.line.setOptions(newOptions);
    }

    polylineObject.info = newPolylineInfo;
  }

  private _createPolyline(lineInfo: IPolylineInfo): void {
    if (lineInfo.latLng.length === 0) {
      return;
    }

    const options: google.maps.PolylineOptions = this._getPolylineOptions(lineInfo);

    const polyline = new google.maps.Polyline(options);
    this._addListenersToPolyline(polyline, lineInfo);

    this._polylines.set(lineInfo.id, {
      info: lineInfo,
      options,
      line: polyline
    });
  }

  private _getPolylineOptions(lineInfo: IPolylineInfo) {
    let options: google.maps.PolylineOptions = {
      ...this.polylineBaseOptions,
      path: lineInfo.latLng,
      map: this.map
    };
    if (lineInfo.isActive !== null) {
      options = lineInfo.isActive ? this._getActivePolylineOptions(options) : this._getInactivePolylineOptions(options);
    }
    return options;
  }

  private _addListenersToPolyline(polyline: google.maps.Polyline, lineInfo: IPolylineInfo): void {
    polyline.addListener('mouseover', () => {
      this._polylines.forEach(poly => {
        this._updatePolyline({ ...poly.info, isActive: poly.line === polyline });
      });
    });

    polyline.addListener('mousemove', (args: any) => {
      const hoverParams: IPolylineHoverParams = {
        polylineInfo: lineInfo,
        trigger: EPolylineHoverTrigger.Polyline,
        hoveredLatLng: new LatLng(args.latLng.lat(), args.latLng.lng())
      };
      this.polylineStartHover.emit(hoverParams);
    });

    polyline.addListener('mouseout', () => {
      this.polylineEndHover.emit();
      this._polylines.forEach(poly => {
        this._updatePolyline({ ...poly.info, isActive: null });
      });
    });
    polyline.addListener('click', () => {
      this.polylineClicked.emit(lineInfo);
    });
  }

  private _getInactivePolylineOptions(options: google.maps.PolylineOptions): google.maps.PolylineOptions {
    return {
      ...options,
      strokeColor: '#B5B5B5',
      zIndex: 9,
      icons: [{ ...options.icons[0], icon: { ...this.lineSymbol, strokeColor: '#D8D8D8' } }]
    };
  }

  private _getActivePolylineOptions(options: google.maps.PolylineOptions): google.maps.PolylineOptions {
    return {
      ...options,
      strokeColor: '#00517B',
      zIndex: 11,
      icons: [{ ...options.icons[0], icon: { ...this.lineSymbol, strokeColor: '#005E8F' } }]
    };
  }

  private _setMapMarkers(markersInfo: MarkerInfo[]): Observable<void> {
    const markersInfoByType = this._getMarkersInfoMapByType(markersInfo);

    const anyUpdates: Observable<boolean>[] = [];
    markersInfoByType.forEach((markers, type) => {
      anyUpdates.push(this._setMapMarkersOfType(markers, type));
    });

    if (anyUpdates.length === 0) {
      return of(void 0);
    }

    return forkJoin(anyUpdates).pipe(
      take(1),
      map(anyChanged => anyChanged.some(changed => changed)),
      map(anyChanged => {
        if (anyChanged) {
          // operations to execute only if any marker changed
          if (this._googleMapsLoaderService.isInfoBoxLoaded() && !isArrayNullOrEmpty(this._plotTypesToShowLabel)) {
            this._showAllLabels();
          }

          this._checkAndExecuteFitToMap();
          this._getPlotsInBoundsAndEmitIfChanged(this.map.getBounds());
        }

        // signal that work is completed
        return void 0;
      })
    );
  }

  private _setMapMarkersOfType(markers: MarkerInfo[], type: EPlotType): Observable<boolean> {
    const markersDiff = this._getMarkersDiff(type, markers);

    if (markersDiff.added.length === 0 && markersDiff.modified.length === 0 && markersDiff.removed.length === 0) {
      return of(false);
    }

    const updateOperations: Observable<Marker>[] = [];

    const markersToRemove = markersDiff.removed;
    if (markersToRemove.length > 0) {
      this._removeMarkers(markersToRemove);
      updateOperations.push(this._updateMarkers(markersToRemove));
    }

    const markersToUpdate = markersDiff.modified;
    if (markersToUpdate.length > 0) {
      updateOperations.push(this._updateMarkers(markersToUpdate));
    }

    const markersToAdd = markersDiff.added;
    if (markersToAdd.length > 0) {
      updateOperations.push(this._addMarkers(markersToAdd));
    }

    if (updateOperations.length === 0) {
      return of(false);
    }

    // wait for all updates operations to complete
    return forkJoin([...updateOperations]).pipe(
      tap(() => {
        if (this._isClusteringEnabledForType(type)) {
          const clusterer = this._clusterersMap.get(type);
          clusterer.repaint();
        }
      }),
      // some markers changed
      map(() => true)
    );
  }

  private _getMarkersDiff(type: EPlotType, markers: MarkerInfo[]): ICollectionDiff<MarkerInfo> {
    let mapDifferByType = this._markersDifferByType.get(type);
    if (isElementNull(mapDifferByType)) {
      mapDifferByType = this._createMarkersDiffer();
      this._markersDifferByType.set(type, mapDifferByType);
    }

    return mapDifferByType.diff(markers);
  }

  private _getPlotsInBoundDiff(type: EAllPlotTypes, markers: MarkerInfo[]): ICollectionDiff<MarkerInfo> {
    let plotsInBoundsDifferByType = this._plotsInBoundsDifferByType.get(type);
    if (isElementNull(plotsInBoundsDifferByType)) {
      plotsInBoundsDifferByType = this._createPlotsInBoundsDiffer();
      this._plotsInBoundsDifferByType.set(type, plotsInBoundsDifferByType);
    }

    return plotsInBoundsDifferByType.diff(markers);
  }

  private _addMarkers(markersToAddInfo: MarkerInfo[]): Observable<Marker> {
    if (this._markerShape) {
      this._markerShape.setMap(undefined);
      this._markerShape = undefined;
    }

    const markers = merge(...markersToAddInfo.map(markerToAddInfo => this._createMarker(markerToAddInfo)));
    return markers.pipe(
      tap(marker => {
        this._addOrUpdateMarkerToMap(marker);

        if (this._isClusteringEnabledForType(marker.markerInfo.type)) {
          this._addMarkerToClusterer(marker);
        }
      })
    );
  }

  private _addMarkerToClusterer(marker: Marker) {
    const clusterer = this._clusterersMap.get(marker.markerInfo.type);
    clusterer.addMarker(marker.googleMarker, true);
  }

  private _addOrUpdateMarkerToMap(marker: Marker) {
    const markersMap = this._getMarkersByIdMap(marker.markerInfo.type);
    markersMap.set(marker.markerInfo.id, marker);
  }

  private _getZIndex(markerInfo: MarkerInfo): number {
    const plotConfig = this._plotTypeConfigs.find(config => config.type === markerInfo.type);
    if (isElementNull(plotConfig) || !plotConfig.zIndex) {
      return this._defaultZIndex;
    }

    const plotZIndex = plotConfig.zIndex;
    let zIndex = plotZIndex instanceof Map ? (<Map<string, number>>plotZIndex).get(markerInfo.options.iconName) : (plotZIndex as number);
    if (!isElementNull(markerInfo.options.priority)) {
      zIndex += markerInfo.options.priority * 10;
    }

    if (zIndex > this._maxMarkerZIndex) {
      this._maxMarkerZIndex = zIndex;
    }
    return zIndex;
  }

  private _getIcon(iconName: string): Observable<string> {
    if (isElementNull(iconName)) {
      return of(null);
    }

    return this._mapIconRegistryService
      .getSerializedIconByName(`ic-${iconName}`)
      .pipe(map(svgXmlString => `data:image/svg+xml;base64,${svgXmlString}`));
  }

  private _updateMarkers(markersToUpdate: MarkerInfo[]): Observable<Marker> {
    return merge(
      ...markersToUpdate.map(markerToUpdateInfo => {
        const currentMarker = this._getMarkerFromMap(markerToUpdateInfo);

        if (isElementNull(currentMarker)) {
          return of(null);
        }

        return this._updateMarker(currentMarker, markerToUpdateInfo).pipe(tap(marker => this._addOrUpdateMarkerToMap(marker)));
      })
    );
  }

  private _getMarkerFromMap(markerToUpdateInfo: MarkerInfo): Marker {
    const markersMap = this._getMarkersByIdMap(markerToUpdateInfo.type);
    const currentMarker = markersMap.get(markerToUpdateInfo.id);
    return currentMarker;
  }

  private _updateMarker(currentMarker: Marker, newMarkerInfo: MarkerInfo): Observable<Marker> {
    if (!this._isClusteringEnabledForType(newMarkerInfo.type)) {
      return this._updateMarkerWithoutClusterer(currentMarker, newMarkerInfo);
    } else {
      return this._updateMarkerWithClusterer(newMarkerInfo, currentMarker);
    }
  }

  // if clustering is enabled then remove and add a new one
  private _updateMarkerWithClusterer(newMarkerInfo: MarkerInfo, currentMarker: Marker): Observable<Marker> {
    const clusterer = this._clusterersMap.get(newMarkerInfo.type);
    clusterer.removeMarker(currentMarker.googleMarker, true);
    return this._createMarker(newMarkerInfo).pipe(
      tap(marker => {
        clusterer.addMarker(marker.googleMarker, true);
      })
    );
  }

  private _updateMarkerWithoutClusterer(markerToUpdate: Marker, newMarkerInfo: MarkerInfo): Observable<Marker> {
    // update reference to the new MarkerInfo
    markerToUpdate.markerInfo = newMarkerInfo;
    // update latlng
    markerToUpdate.googleMarker.setLatLng(newMarkerInfo.latLng.toGoogleMaps());

    const hasIconChanged = !isStringNullOrEmpty(markerToUpdate.markerInfo.options.iconName);

    // update class if changed
    if (newMarkerInfo.options.className !== undefined || hasIconChanged) {
      const newMarkerClassName = this._getClassNameFromMarker(newMarkerInfo);
      markerToUpdate.googleMarker.setClassName(newMarkerClassName);
    }
    // update zIndexModifier if changed
    if (!isElementNull(newMarkerInfo.options.priority)) {
      const zIndex = this._getZIndex(newMarkerInfo);
      markerToUpdate.googleMarker.setZIndex(zIndex);
    }

    return !hasIconChanged ? of(markerToUpdate) : this._updateMarkerIcon(newMarkerInfo, markerToUpdate);
  }

  private _updateMarkerIcon(newMarkerInfo: MarkerInfo, markerToUpdate: Marker): Observable<Marker> {
    const plotConfig = newMarkerInfo.combinedPlotType
      ? this._plotTypeConfigs.find(config => config.type == newMarkerInfo.combinedPlotType)
      : this._plotTypeConfigs.find(config => config.type === newMarkerInfo.type);
    const size = this._getSize(plotConfig, newMarkerInfo);
    return this._getIcon(newMarkerInfo.options.iconName).pipe(
      map(icon => {
        markerToUpdate.googleMarker.setSize(size);
        markerToUpdate.googleMarker.setIcon(icon);
        return markerToUpdate;
      })
    );
  }

  private _addListneresToMarker(marker: Marker): void {
    this._markerMouseOverEvent(marker);
    this._markerMouseOutEvent(marker);
    this._markerClickEvent(marker);
    this._markerRightClickEvent(marker);
  }

  private _markerRightClickEvent(marker: Marker): void {
    marker.googleMarker.addListener('rightclick', (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setTimeout(() => {
        if (this._markerShape) {
          this._markerShape.setMap(undefined);
          this._markerShape = undefined;
        }
        const overlay = new google.maps.OverlayView();
        overlay.onAdd = () => {
          const point: IPoint = {
            x: event.clientX,
            y: event.clientY - this._mapHostOffsetTop
          };
          const updatedMarkerInfo = this._findUpdatedMarkerInfo(marker.markerInfo);
          const rightClickLatLng = updatedMarkerInfo.latLng.toGoogleMaps();
          const streetViewService = new google.maps.StreetViewService();
          streetViewService.getPanoramaByLocation(rightClickLatLng, 50, (data: any, status: any) => {
            const clickLatLng = new LatLng(rightClickLatLng.lat(), rightClickLatLng.lng());
            const menuData: IMapContextMenuData<MarkerInfo> = {
              clickPosition: point,
              clickLatLng,
              streetViewAvailable: false,
              markerInfo: updatedMarkerInfo
            };
            this._zone.run(() => {
              menuData.streetViewAvailable = status === google.maps.StreetViewStatus.OK;
              this.markerRightClicked.emit(menuData);
            });
          });
        };
        overlay.setMap(this.map);
      });
    });
  }

  private _markerClickEvent(marker: Marker): void {
    marker.googleMarker.addListener('click', (event: MouseEvent) => {
      const point = {
        x: event.x,
        y: event.y - this._mapHostElement.nativeElement.getBoundingClientRect().top
      };
      this._zone.run(() => {
        const updatedMarkerInfo = this._findUpdatedMarkerInfo(marker.markerInfo);
        this.markerClicked.emit({ point, data: updatedMarkerInfo });
      });
    });
  }

  private _markerMouseOutEvent(marker: Marker): void {
    marker.googleMarker.addListener('mouseout', () => {
      if (this._shouldBringMarkerToForegroundOnHover(marker) && !this._isCurrentSelectedMarker(marker)) {
        marker.googleMarker.setZIndex(this._getZIndex(marker.markerInfo));
      }
      if (this._markerShape) {
        this._markerShape.setMap(undefined);
        this._markerShape = undefined;
      }
      this._zone.run(() => {
        const updatedMarkerInfo = this._findUpdatedMarkerInfo(marker.markerInfo);
        this._applyMarkerDependenciesHighlightEvent(updatedMarkerInfo, false);
        this.currentPlotChanged.emit();
        this.currentPlotEndHover.emit();
      });
    });
  }

  private _isCurrentSelectedMarker(marker: Marker): boolean {
    if (marker?.markerInfo) {
      return marker.markerInfo.id === this._selectedMarkerPositionHighlightOverlay?.marker?.id;
    }

    return false;
  }

  private _markerMouseOverEvent(marker: Marker): void {
    marker.googleMarker.addListener('mouseover', (mouseEvent: MouseEvent) => {
      mouseEvent.preventDefault();
      mouseEvent.stopPropagation();

      if (this._shouldBringMarkerToForegroundOnHover(marker) && !this._isCurrentSelectedMarker(marker)) {
        marker.googleMarker.setZIndex(this._maxMarkerZIndex + 1);
      }

      const updatedMarkerInfo = this._findUpdatedMarkerInfo(marker.markerInfo);
      if (isElementNull(updatedMarkerInfo)) {
        // marker was removed from the map
        return;
      }

      if (updatedMarkerInfo.shape) {
        this._markerShape = updatedMarkerInfo.shape;
        this._markerShape.setMap(this.map);
      }
      this._zone.run(() => {
        if (
          !this._labelInfo.find(infoBox => infoBox.plotId === updatedMarkerInfo.id) &&
          !(this._balloon && this._balloon.id === updatedMarkerInfo.id && this._balloon.type === updatedMarkerInfo.type)
        ) {
          this.currentPlotChanged.emit(updatedMarkerInfo);
        }
        this._applyMarkerDependenciesHighlightEvent(updatedMarkerInfo, true);
        this.currentPlotStartHover.emit(updatedMarkerInfo);
      });
    });
  }

  private _getMarkerOffset(markerInfo: MarkerInfo, plotConfig: IPlotConfig) {
    return !isElementNull(markerInfo.options.offset)
      ? new google.maps.Point(markerInfo.options.offset.width, markerInfo.options.offset.height)
      : new google.maps.Point(plotConfig.offset.width, plotConfig.offset.height);
  }

  private _createMarker(markerInfo: MarkerInfo): Observable<Marker> {
    const plotConfig = markerInfo.combinedPlotType
      ? this._plotTypeConfigs.find(config => config.type === markerInfo.combinedPlotType)
      : this._plotTypeConfigs.find(config => config.type === markerInfo.type);

    const size = this._getSize(plotConfig, markerInfo);
    const offsetSize = this._getMarkerOffset(markerInfo, plotConfig);
    const zIndex = this._getZIndex(markerInfo);
    const markerClassName = this._getClassNameFromMarker(markerInfo);
    return this._getIcon(markerInfo.options.iconName).pipe(
      map(icon => {
        const markerInstance = new CustomMarker(
          markerInfo.latLng.toGoogleMaps(),
          this._isClusteringEnabledForType(markerInfo.type) ? null : this.map,
          !isElementNull(icon) ? icon : undefined,
          size,
          {
            // args
            markerId: this._getCustomMarkerId(markerInfo),
            offset: offsetSize,
            className: markerClassName,
            htmlContent: markerInfo.options.htmlContent,
            zIndex,
            rotate: markerInfo.options.rotate
          }
        );

        const marker: Marker = {
          markerInfo,
          googleMarker: markerInstance
        };

        // true by default
        const addListeners =
          !isElementNull(markerInfo.options) && markerInfo.options.addListeners !== undefined ? markerInfo.options.addListeners : true;

        if (addListeners) {
          this._addListneresToMarker(marker);
        }
        return marker;
      })
    );
  }

  private _getSize(plotConfig: IPlotConfig, markerInfo: MarkerInfo): google.maps.Size {
    const size = !isElementNull(markerInfo.options.size) ? markerInfo.options.size : plotConfig.size;
    return new google.maps.Size(size.width, size.height);
  }

  private _getCustomMarkerId(markerInfo: MarkerInfo): string {
    return `${markerInfo.type}_${markerInfo.id}`;
  }

  private _getMarkerIdFromCustomMarker(customMarker: CustomMarker): number {
    const customMarkerId = customMarker.getId() as string;
    if (isElementNull(customMarkerId)) {
      return null;
    }

    return +customMarkerId.substring(customMarkerId.indexOf('_') + 1);
  }
  private _findUpdatedMarkerInfo(markerInfo: MarkerInfo): MarkerInfo {
    if (this._markersByType.has(markerInfo.type)) {
      const markersByType = this._markersByType.get(markerInfo.type);
      if (markersByType.has(markerInfo.id)) {
        return markersByType.get(markerInfo.id).markerInfo;
      }
    }
    // not found
    return null;
  }

  private _applyMarkerDependenciesHighlightEvent(markerElem: MarkerInfo, isAdding: boolean): void {
    const depToApply = this._plotTypeDependencies.filter(dependency => dependency.plotTypeOrigin === markerElem.type);
    depToApply.forEach(dependency => {
      if (!this._markersByType.has(dependency.plotTypeTarget)) {
        return;
      }

      const markersByType = this._markersByType.get(dependency.plotTypeTarget);
      Array.from(markersByType.values()).forEach(marker => {
        if (
          marker.markerInfo.type === dependency.plotTypeTarget &&
          !isElementNull(dependency.targetLinkValue(marker.markerInfo)) &&
          dependency.targetLinkValue(marker.markerInfo) === dependency.originLinkValue(markerElem)
        ) {
          if (isAdding) {
            this._handleMarkerHighlight(marker.markerInfo);
          } else {
            this._removeHighlight();
          }
        }
      });
    });
  }

  private _showAllLabels(): void {
    this._hideLabels();

    if (isElementNull(this._maxPlottableMarkers)) {
      return;
    }

    const maxPlottableMarkers = this._maxPlottableMarkers.find(marker => marker.type === EPlotType.balloon);
    const isTooManyLabels = isElementNull(maxPlottableMarkers) || this._markersLabels.length > maxPlottableMarkers.max;
    if (isTooManyLabels) {
      return;
    }

    let markersToShowLabel: any[] = [];
    this._plotTypesToShowLabel.forEach(plotType => {
      if (!this._markersByType.has(plotType)) {
        return;
      }

      const markers = this._markersByType.get(plotType);
      const markerByConfig = Array.from(markers.values()).map(marker => ({
        marker,
        infoBoxOptions: this._plotTypeConfigs.find(config => config.type === plotType).infoBoxOptions
      }));
      markersToShowLabel = markersToShowLabel.concat(markerByConfig);
    });

    if (!isArrayNullOrEmpty(Array.from(this._plotClusterConfig.entries()))) {
      const markersClustered = this._markersClustered();
      const markersToHideLabel = markersToShowLabel.filter(markerToHideLabel =>
        markersClustered.includes(markerToHideLabel.marker.googleMarker)
      );

      markersToShowLabel = markersToShowLabel.filter(
        markerToShowLabel => !markersClustered.includes(markerToShowLabel.marker.googleMarker)
      );

      markersToHideLabel.forEach(markerToHideLabel => this._hideLabel(markerToHideLabel.marker.markerInfo.id));
    }

    markersToShowLabel.forEach(markerToShow => {
      const markerLabel = this._markersLabels.find((label: MarkerLabel) => label.id === markerToShow.marker.markerInfo.id);
      this._showLabel(markerLabel, markerToShow.marker, markerToShow.infoBoxOptions);
    });
  }

  private _showLabel(markerLabel: MarkerLabel, marker: Marker, infoBoxOptions?: InfoBoxOptions): void {
    infoBoxOptions = { ...defaultLabelInfoBoxOptions, ...infoBoxOptions };
    const exists = this._labelInfo.some((infoBox: ILabelInfo) => infoBox.plotId === marker.markerInfo.id);

    const isLabelNullOrWhitespace = (label: MarkerLabel) =>
      isElementNull(label) || label.data.every(d => isElementNull(d) || d.trim().length === 0);

    if (!exists && !isLabelNullOrWhitespace(markerLabel)) {
      this._createInfoBoxLabel(infoBoxOptions, marker, markerLabel);
    }
  }

  private _hideLabels(excludedTypes?: number[]): void {
    let labelsToHide: ILabelInfo[];
    if (!isArrayNullOrEmpty(excludedTypes)) {
      labelsToHide = this._getLabelsToHideByAllowedHiddenTypes();
    } else {
      labelsToHide = this._labelInfo.filter(labelInfo => labelInfo.plotId !== this._zoomLabelPlotId);
    }

    labelsToHide.forEach(labelInfo => {
      this._hideLabel(labelInfo.plotId);
    });
  }

  private _getLabelsToHideByAllowedHiddenTypes() {
    const typesAllowedToBeHidden = Array.from(this._markersByType.keys()).filter(type => !this._plotTypesToShowLabel.includes(type));

    const isMarkerAllowedToBeHidden = (plotId: number): boolean => {
      let isAllowed = false;
      for (const [type, markersById] of this._markersByType.entries()) {
        if (typesAllowedToBeHidden.includes(type) && markersById.has(plotId)) {
          isAllowed = true;
          break;
        }
      }
      return isAllowed;
    };

    return this._labelInfo.filter(labelInfo => labelInfo.plotId !== this._zoomLabelPlotId && isMarkerAllowedToBeHidden(labelInfo.plotId));
  }

  private _hideLabel(markerId: number): void {
    const labelToHideIndex = this._labelInfo.findIndex(labelInfo => labelInfo.plotId === markerId);
    const labelToHide = this._labelInfo[labelToHideIndex];
    if (!isElementNull(labelToHide)) {
      labelToHide.infoBox.close();
      labelToHide.component.destroy();
      labelToHide.component = null;
      this._labelInfo.splice(labelToHideIndex, 1);
    }
  }

  private _createInfoBoxLabel(infoBoxOptions: InfoBoxOptions, marker: Marker, markerLabel: MarkerLabel): void {
    const data = markerLabel.data.filter(value => !isStringNullOrEmpty(value.trim()));
    const labelComponent = this._createLabelComponent(data);
    const labelMarginBottom = infoBoxOptions.plotMargin;
    const options = {
      ...infoBoxOptions,
      position: marker.googleMarker.getPosition(),
      content: labelComponent.location.nativeElement,
      isHidden: true,
      alignBottom: true
    };

    const infoBox = new InfoBox(options);
    infoBox.setVisible(false);
    infoBox.open(this.map, marker.googleMarker);
    google.maps.event.addListener(infoBox, 'domready', () => {
      this._zone.run(() => {
        const labelComponentPosition = -(labelComponent.location.nativeElement.parentElement.offsetWidth / 2);

        const opts = {
          pixelOffset: new google.maps.Size(labelComponentPosition, labelMarginBottom)
        };

        labelComponent.location.nativeElement.style.visibility = 'visible';
        labelComponent.location.nativeElement.style.opacity = 1;
        infoBox.setOptions(opts);
        infoBox.setVisible(true);
      });
    });

    this._labelInfo.push({
      plotId: marker.markerInfo ? marker.markerInfo.id : this._zoomLabelPlotId,
      infoBox,
      component: labelComponent
    });
  }

  private _createLabelComponent(data: string[]): ComponentRef<LabelComponent> {
    const lineHeight = 16;
    const minHeight = 24;
    const verticalPadding = 8;
    const top = data.length === 0 ? minHeight : verticalPadding + lineHeight;

    const component = this._viewContainerRef.createComponent(LabelComponent);

    component.instance.data = data;
    component.instance.top = top;
    component.location.nativeElement.style.visibility = 'hidden';
    component.location.nativeElement.style.opacity = 0;
    component.changeDetectorRef.detectChanges();

    return component;
  }

  private _removeMarkers(markersToRemove: MarkerInfo[]) {
    markersToRemove.forEach(markerToRemove => {
      const markersMap = this._getMarkersByIdMap(markerToRemove.type);
      const marker = markersMap.get(markerToRemove.id);

      if (isElementNull(marker)) {
        return;
      }

      this._removeMarker(marker, markersMap);
    });
  }

  private _removeMarker(marker: Marker, markersById: Map<number, Marker>) {
    if (!this._isClusteringEnabledForType(marker.markerInfo.type)) {
      marker.googleMarker.setMap(undefined);
    } else {
      const clusterer = this._clusterersMap.get(marker.markerInfo.type);
      clusterer.removeMarker(marker.googleMarker, true);
    }

    google.maps.event.clearInstanceListeners(marker.googleMarker);
    markersById.delete(marker.markerInfo.id);
    if (!isElementNull(this._balloon) && marker.markerInfo.id === this._balloon.id && marker.markerInfo.type === this._balloon.type) {
      this._closeBalloon();
    }
  }

  private _checkAndExecuteFitToMap(): void {
    if (this.shouldFitToMap) {
      this.fitToMap();
    }
  }

  private _setMapBoundsWithOffset(bounds: google.maps.LatLngBounds, maxZoomLevel: number): void {
    const isInvalidBounds = bounds?.getCenter()?.lat() === 0 && bounds?.getCenter()?.lng() === -180;
    if (!isInvalidBounds) {
      const zoomLevel = this._getZoomLevelForBounds(maxZoomLevel, bounds);
      if (isElementNull(zoomLevel)) {
        return;
      }

      if (this.map.getZoom() !== zoomLevel) {
        this.map.setZoom(zoomLevel);
      }
      this._setOffsetCenter(bounds.getCenter());
      this._emitMapBounds();
      this._updateCenter();
    }
  }

  private _getZoomLevelForBounds(maxZoomLevel: number, bounds: google.maps.LatLngBounds): number {
    // some race conditions can lead to this method being called before map dimensions were set
    if (this._mapDimensions.height === 0 && this._mapDimensions.width === 0) {
      this._updateMapDimensions();
    }

    const dimensions = {
      width: this._mapDimensions.width - this._mapOffset.left - this._mapOffset.right,
      height: this._mapDimensions.height - this._mapOffset.top - this._mapOffset.bottom
    };

    if (!this._areDimensionsValid(dimensions)) {
      return null;
    }

    return isElementNull(maxZoomLevel)
      ? this._getBoundsZoomLevel(bounds, dimensions)
      : Math.min(maxZoomLevel, this._getBoundsZoomLevel(bounds, dimensions));
  }

  private _updateMapOffset(): void {
    const bufferMargin = 10;
    this._mapOffset = new Position(
      this._overlayMargins.top + bufferMargin,
      this._overlayMargins.right + bufferMargin,
      this._overlayMargins.bottom + bufferMargin,
      this._overlayMargins.left + bufferMargin
    );
  }

  private _areDimensionsValid(dimensions: ISize): boolean {
    return dimensions.height >= 0 && dimensions.width >= 0;
  }

  private _getBoundsZoomLevel(bounds: google.maps.LatLngBounds, dimensions: ISize): number {
    const tileSize: ISize = { height: 256, width: 256 }; // google World tile size
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const lngDiff = ne.lng() - sw.lng();

    const latRadian = (lat: number) => {
      const sin = Math.sin((lat * Math.PI) / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };
    const zoom = (mapPx: number, worldPx: number, fraction: number) => Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);

    const latFraction = (latRadian(ne.lat()) - latRadian(sw.lat())) / Math.PI;
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(dimensions.height, tileSize.height, latFraction);
    const lngZoom = zoom(dimensions.width, tileSize.width, lngFraction);
    return Math.min(latZoom, lngZoom, this._defaultZoom);
  }

  private _setOffsetCenter(latlng: google.maps.LatLng): void {
    const offsetX = (this._mapOffset.left - this._mapOffset.right) / 2;
    const offsetY = (this._mapOffset.bottom - this._mapOffset.top) / 2;
    const newCenterLatLng = this._offsetLatLng(latlng, offsetX, offsetY);
    if (this.map.getCenter() !== newCenterLatLng) {
      this.map.panTo(newCenterLatLng);
    }
  }

  private _offsetLatLng(latlng: google.maps.LatLng, offsetX: number, offsetY: number): google.maps.LatLng {
    offsetX = offsetX || 0;
    offsetY = offsetY || 0;
    const scale = Math.pow(2, this.map.getZoom());
    const point = this.map.getProjection().fromLatLngToPoint(latlng);
    const pixelOffset = new google.maps.Point(offsetX / scale, offsetY / scale);
    const newPoint = new google.maps.Point(point.x - pixelOffset.x, point.y + pixelOffset.y);
    return this.map.getProjection().fromPointToLatLng(newPoint);
  }

  private _toggleTrafficLayer(): void {
    if (this._isTrafficEnabled) {
      this._trafficLayer = new google.maps.TrafficLayer();
      this._trafficLayer.setMap(this.map);
    } else if (!isElementNull(this._trafficLayer)) {
      this._trafficLayer.setMap(undefined);
      this._trafficLayer = undefined;
    }
  }

  private _updateCenter(): void {
    if (this._isUpdatingMapCenter) {
      return;
    }
    this._isUpdatingMapCenter = true;
    const currentCenter = new LatLng(this.map.getCenter().lat(), this.map.getCenter().lng());

    if (!this._mapCenter.equals(currentCenter)) {
      this.mapCenterChanged.emit(currentCenter);
    }
    this._isUpdatingMapCenter = false;
  }

  private _setPanoramaLocation(location: LatLng) {
    const panorama = this.map.getStreetView();
    panorama.setPosition(location);
    panorama.setPov({
      heading: 0,
      pitch: 0
    });
    panorama.setZoom(this._streetViewZoom);
    panorama.setVisible(true);
  }

  private _setZoomToMarker(location: ILatLng): void {
    this._isAddingZoomToMarker = true;
    this._zoomToLatLng(location);
    setTimeout(() => {
      this._isAddingZoomToMarker = false;
    });
  }

  private _setZoomToLocation(location: ILatLng): void {
    this._zoomToLatLng(location);

    const GeoCoder = new google.maps.Geocoder();
    GeoCoder.geocode({ location }, (results, status) => {
      let addressString = '';
      if (status === google.maps.GeocoderStatus.OK) {
        addressString = results[0].formatted_address;
        this._zoomToLocationMarker = new CustomMarker(
          new LatLng(location.lat, location.lng).toGoogleMaps(),
          this.map,
          `assets/images/plots/ic-plot-locationPin.svg`,
          new google.maps.Size(28, 28),
          {
            markerId: this._zoomPlotId,
            offset: new google.maps.Point(14, 13)
          }
        );
        this._createInfoBoxLabel(
          defaultLabelInfoBoxOptions,
          {
            markerInfo: null,
            googleMarker: this._zoomToLocationMarker
          },
          {
            data: [addressString],
            id: this._zoomLabelPlotId
          }
        );
      }
    });
  }

  private _zoomToLatLng(location: ILatLng, zoom: number = this._defaultZoom): void {
    this.map.panTo(new google.maps.LatLng(location));
    this.map.setZoom(zoom);

    if (this._zoomToLocationMarker) {
      this._zoomToLocationMarker.setMap(undefined);
      this._hideLabel(this._zoomLabelPlotId);
    }
  }

  private _clearComponentsFromClusteredMarkers(plotType?: number): void {
    const markersClustered = this._markersClustered(plotType);
    markersClustered.forEach((markerFromCluster: CustomMarker) => {
      let markerToClear: Marker;
      if (!isElementNull(plotType)) {
        const id = this._getMarkerIdFromCustomMarker(markerFromCluster);
        if (isElementNull(id)) {
          return;
        }

        markerToClear = this._findMarkerByTypeAndId(plotType, id);
      } else {
        markerToClear = this._findMarkerByReference(markerFromCluster);
      }

      if (!isElementNull(markerToClear)) {
        this._hideLabel(markerToClear.markerInfo.id);

        if (markerToClear.markerInfo.id === this._balloon?.id) {
          this._closeBalloon();
        }
      }
    });
  }
  private _findMarkerByReference(markerFromCluster: CustomMarker): Marker {
    for (const markersMap of this._markersByType.values()) {
      for (const marker of markersMap.values()) {
        if (marker.googleMarker === markerFromCluster) {
          return marker;
        }
      }
    }
    return null;
  }

  private _markersClustered(plotType?: number): CustomMarker[] {
    const markersClustered: CustomMarker[] = [];

    const clusterers = isElementNull(plotType) ? Array.from(this._clusterersMap.values()) : [this._clusterersMap.get(plotType)];

    clusterers.forEach(clusterer => {
      // if the map is more zoomed than the cluster max zoom, then markers are not actually clustered.
      if (this.map.getZoom() > clusterer.getMaxZoom()) {
        return;
      }

      // get internal clusters and extract the markers inside.
      clusterer.getClusters().forEach((cluster: any) => {
        if (cluster.getSize() >= clusterer.getMinimumClusterSize()) {
          markersClustered.push(...cluster.getMarkers());
        }
      });
    });

    return markersClustered;
  }

  private _closeBalloon(shouldEmit = true): void {
    if (isElementNull(this._balloon)) {
      return;
    }

    if (this._selectedMarkerPositionHighlightOverlay?.marker.id === this._balloon.id) {
      this._removeSelectedHighlight();
    }

    this._balloon.infoBox.close();
    this._balloon.component.destroy();
    this._balloon = null;
    if (shouldEmit) {
      this.balloonClosed.emit();
    }
    this._allowMapDragAndScroll(true);
  }

  private _showBalloon(balloon: IBalloon): void {
    if (!isElementNull(this._balloon) && this._balloon.id === balloon.id && this._balloon.type === balloon.type) {
      this._balloon.component.instance.balloon = balloon;
      this._balloon.component.changeDetectorRef.detectChanges();
      this._balloon.infoBoxOptions.position = new google.maps.LatLng(balloon.latLng.lat, balloon.latLng.lng);
      balloon.point = null;
      this._updateBalloonPosition(balloon);
    } else {
      this._closeBalloon(false);
      const balloonMarker = this._findMarkerByTypeAndId(balloon.type, balloon.id);

      this._createBalloon(balloon);
      this._balloon.infoBox.setVisible(false);
      if (!isElementNull(balloonMarker)) {
        this._balloon.infoBox.open(this.map, balloonMarker.googleMarker);
      } else {
        this._balloon.infoBox.open(this.map);
      }
      this._setupBalloonInfoBoxListeners(balloon);
    }
  }

  private _setupBalloonInfoBoxListeners(balloon: IBalloon) {
    google.maps.event.addListener(this._balloon.infoBox, 'domready', () => {
      this._zone.run(() => {
        this._updateBalloonPosition(balloon);
        if (this._balloon.type !== EPlotType.replay) {
          // this._balloon.component.location.nativeElement.classList.add('d-none');
          // this._balloon.component.location.nativeElement.classList.add('d-md-block');
        } else {
          // this._balloon.component.location.nativeElement.classList.add('d-block');
        }

        this._balloon.infoBox.setVisible(true);

        // To allow scrolling within balloons, we need to disable map scrolling and dragging while the mouse is in the balloon
        this._balloon.infoBox.div_.addEventListener('mouseenter', () => {
          this._allowMapDragAndScroll(false);
        });

        this._balloon.infoBox.div_.addEventListener('mouseleave', () => {
          this._allowMapDragAndScroll(true);
        });
      });
    });
  }

  private _allowMapDragAndScroll(allow: boolean): void {
    this.map.setOptions({
      draggable: allow,
      scrollwheel: allow
    });
  }
  private _createBalloon(balloonInfo: IBalloon): void {
    const component = this._viewContainerRef.createComponent(BalloonComponent);
    component.instance.balloon = balloonInfo;
    component.instance.contentTemplate = this.balloonTemplate;
    component.location.nativeElement.style.visibility = 'hidden';
    component.location.nativeElement.style.opacity = 0;
    component.changeDetectorRef.detectChanges();
    component.instance.closed.pipe(take(1)).subscribe(() => {
      this._closeBalloon();
    });

    const options = {
      ...this._defaultBalloonInfoBoxOptions,
      content: component.location.nativeElement,
      position: new google.maps.LatLng(balloonInfo.latLng.lat, balloonInfo.latLng.lng)
    };

    const infoBox = new InfoBox(options);
    this._balloon = {
      component,
      id: balloonInfo.id,
      infoBox,
      infoBoxOptions: options,
      type: balloonInfo.type
    };
  }

  private _updateBalloonPosition(balloon: IBalloon): void {
    const mapWidth = this._mapDimensions.width;
    const balloonElement = this._balloon.component.location.nativeElement;
    const balloonHeight = balloonElement.offsetHeight;
    const balloonWidth = balloonElement.offsetWidth;

    let plotConfig = this._plotTypeConfigs.find(config => config.type === balloon.marker.type);
    if (isElementNull(plotConfig)) {
      plotConfig = {
        offset: balloon.marker.options.offset,
        size: balloon.marker.options.size
      } as IPlotConfig;
    }
    const { width: markerHorizontalOffset, height: markerVerticalOffset } = plotConfig.offset;
    const { width: markerWidth, height: markerHeight } = plotConfig.size;

    const balloonSeparation = 4;
    const balloonSpikeSize = 8;

    const balloonOffset = {
      top: markerVerticalOffset + balloonSeparation + balloonSpikeSize,
      right: markerWidth - markerHorizontalOffset + balloonSeparation + balloonSpikeSize,
      bottom: markerHeight - markerVerticalOffset + balloonSeparation + balloonSpikeSize,
      left: markerHorizontalOffset + balloonSeparation + balloonSpikeSize
    };

    // always recalc point, otherwise after bounds change the balloon will not popup in right position
    balloon.point = this._fromLatLngToPoint(balloon.latLng);

    const topSpace = balloon.point.y - balloonHeight - this._overlayMargins.top - balloonOffset.top;
    const rightSpace = mapWidth - balloon.point.x - balloonWidth - this._overlayMargins.right - balloonOffset.right;
    const leftSpace = balloon.point.x - balloonWidth - this._overlayMargins.left - balloonOffset.left;

    let pixelOffset: google.maps.Size;
    let position: EOrientation;
    let alignBottom = true;
    if (topSpace > 0) {
      if (leftSpace > -balloonWidth / 2 && rightSpace > -balloonWidth / 2) {
        pixelOffset = new google.maps.Size(-balloonWidth / 2, -balloonOffset.top);
        position = EOrientation.top;
      } else if (leftSpace <= -balloonWidth / 2) {
        pixelOffset = new google.maps.Size(balloonOffset.right, balloonHeight / 2);
        position = EOrientation.right;
      } else {
        pixelOffset = new google.maps.Size(-balloonWidth - balloonOffset.left, balloonHeight / 2);
        position = EOrientation.left;
      }
    } else {
      alignBottom = false;
      if (leftSpace > -balloonWidth / 2 && rightSpace > -balloonWidth / 2) {
        pixelOffset = new google.maps.Size(-balloonWidth / 2, balloonOffset.bottom);
        position = EOrientation.bottom;
      } else if (leftSpace <= -balloonWidth / 2) {
        pixelOffset = new google.maps.Size(balloonOffset.right, -balloonHeight / 2);
        position = EOrientation.right;
      } else {
        pixelOffset = new google.maps.Size(-balloonWidth - balloonOffset.left, -balloonHeight / 2);
        position = EOrientation.left;
      }
    }

    this._balloon.component.instance.position = position;

    const opts = {
      ...this._balloon.infoBoxOptions,
      position: new google.maps.LatLng(balloon.latLng.lat, balloon.latLng.lng),
      alignBottom,
      pixelOffset
    };

    delete opts.content;

    this._balloon.component.location.nativeElement.style.visibility = 'visible';
    this._balloon.component.location.nativeElement.style.opacity = 1;
    this._balloon.infoBox.setOptions(opts);
  }

  private _fromLatLngToPoint(latLng: LatLng) {
    const topRight = this.map.getProjection().fromLatLngToPoint(this.map.getBounds().getNorthEast());
    const bottomLeft = this.map.getProjection().fromLatLngToPoint(this.map.getBounds().getSouthWest());
    const scale = Math.pow(2, this.map.getZoom());
    const worldPoint = this.map.getProjection().fromLatLngToPoint(latLng.toGoogleMaps());
    return new google.maps.Point((worldPoint.x - bottomLeft.x) * scale, (worldPoint.y - topRight.y) * scale);
  }

  private _setMapType(): void {
    if (this._mapType === EMapTypeId.SATURATED) {
      this.map.setMapTypeId(EMapTypeId.ROADMAP);
      this.map.setOptions({ styles: saturatedMapStyle });
    } else {
      this.map.setMapTypeId(this._mapType);
      this.map.setOptions({ styles: null });
    }
  }

  private _handleMarkerHighlight(marker: MarkerInfo): void {
    const isEmptyMarker = isElementNull(marker);
    const previousHighlightExists = !isElementNull(this._positionHighlightOverlay);

    // empty marker means remove the previous highlight
    // no previous highlight exists - do nothing
    if (isEmptyMarker && !previousHighlightExists) {
      return;
    }

    // remove the previous highlight if it exists
    if (previousHighlightExists) {
      this._positionHighlightOverlay.highlight.setMap(null);
      this._positionHighlightOverlay = null;
    }

    // if the selected marker is already highlighted, do not draw a second highlight.
    // overlapping highlights with opacity result in a darker color
    const shouldDrawHighlight = !isEmptyMarker && marker.id !== this._selectedMarkerPositionHighlightOverlay?.marker?.id;
    if (shouldDrawHighlight) {
      this._positionHighlightOverlay = {
        highlight: this._drawHighlightOnMarker(marker),
        marker
      };
    }
  }

  private _handleSelectedMarkerZIndex(marker: MarkerInfo): void {
    const previousMarkerInfo = this._selectedMarkerPositionHighlightOverlay?.marker;
    if (previousMarkerInfo) {
      const previousMarker = this._getMarkerFromMap(previousMarkerInfo);
      if (previousMarker?.googleMarker) {
        previousMarker.googleMarker.setZIndex(this._getZIndex(previousMarkerInfo));
      }
    }

    if (marker) {
      const currentSelectedMarker = this._getMarkerFromMap(marker);
      if (currentSelectedMarker?.googleMarker) {
        currentSelectedMarker.googleMarker.setZIndex(this._maxMarkerZIndex);
      }
    }
  }

  private _handleSelectedMarkerHighlight(marker: MarkerInfo): void {
    const isEmptyMarker = isElementNull(marker) || Object.keys(marker).length === 0;
    const previousSelectedHighlightExists = !isElementNull(this._selectedMarkerPositionHighlightOverlay);

    // empty marker means remove the previous highlight
    // no previous highlight exists - do nothing
    if (isEmptyMarker && !previousSelectedHighlightExists) {
      return;
    }

    // if a previous highlight exists we need to remove it. scenarios:
    // an empty marker or
    // selected marker is differnet than the previous selected marker
    if (previousSelectedHighlightExists) {
      this._selectedMarkerPositionHighlightOverlay.highlight.setMap(null);
      this._selectedMarkerPositionHighlightOverlay = null;
    }

    // when we select a marker, we are already hovering the plot and so it is highlighted.
    // we need to remove it otherwise we would get two overlapping highlights
    const previousHighlightExists = !isElementNull(this._positionHighlightOverlay);
    if (previousHighlightExists) {
      this._positionHighlightOverlay.highlight.setMap(null);
      this._positionHighlightOverlay = null;
    }

    // draw a new highlight
    if (!isEmptyMarker) {
      this._selectedMarkerPositionHighlightOverlay = {
        highlight: this._drawHighlightOnMarker(marker),
        marker
      };
    }
  }

  private _drawHighlightOnMarker(marker: MarkerInfo): PositionHighlightOverlay {
    let highlightPosition = new google.maps.LatLng(marker.latLng.lat, marker.latLng.lng);
    if (this._isClusteringEnabled() && marker.type !== EPlotType.cluster) {
      // If the icon is part of a cluster, use the cluster position
      const cluster = this._getClusterForMarker(marker);
      if (cluster) {
        highlightPosition = cluster.getCenter();
      }
    }

    const highlight: PositionHighlightOverlay = new PositionHighlightOverlay(
      {
        lat: highlightPosition.lat(),
        lng: highlightPosition.lng(),
        iconName: marker.options?.iconName
      },
      this.map,
      this._mapHostElement.nativeElement
    );

    highlight.setMap(this.map);

    return highlight;
  }

  private _getClusterForMarker(marker: MarkerInfo): Cluster {
    const googleMarker = this._markersByType.get(marker.type)?.get(marker.id)?.googleMarker as unknown as google.maps.Marker;
    const clusterForMarker = this._clusterersMap
      .get(marker.type)
      ?.getClusters()
      ?.find(cluster => cluster.isMarkerInClusterBounds(googleMarker));
    return clusterForMarker;
  }

  private _toggleClustering(): void {
    if (!this._googleMapsLoaderService.isGoogleMapsLoaded()) {
      return;
    }
    const wasClusteringEnabled = !this._isClusteringEnabled();
    if (wasClusteringEnabled) {
      this._clusterersMap.forEach(cluster => cluster.clearMarkers());
    }

    const isClusteringEnabled = !wasClusteringEnabled;
    if (isClusteringEnabled) {
      this._plotClusterConfig.forEach(plotTypes => {
        plotTypes.forEach(plotType => {
          this._addAllMarkersToCluster(plotType);
        });
      });
    } else {
      // add markers to map directly
      this._markersByType.forEach(markerMap => Array.from(markerMap.values()).forEach(marker => marker.googleMarker.setMap(this.map)));

      if (!isArrayNullOrEmpty(this._plotTypesToShowLabel)) {
        this._showAllLabels();
      }

      if (this._balloon?.type === EPlotType.cluster) {
        this._closeBalloon();
      }
    }
  }

  private _addAllMarkersToCluster(plotType: EPlotType): void {
    const markersMap = this._markersByType.get(plotType);
    if (!isElementNull(markersMap)) {
      const markersToCluster = Array.from(markersMap.values()).map(marker => marker.googleMarker);
      if (markersToCluster.length > 0) {
        const clusterer = this._clusterersMap.get(plotType);
        if (!isElementNull(clusterer)) {
          clusterer.addMarkers(markersToCluster);
        }
      }
    }
  }

  private _findMarkerById(id: number): Marker {
    for (const markersById of this._markersByType.values()) {
      const marker = markersById.get(id);
      if (!isElementNull(marker)) {
        return marker;
      }
    }

    return null;
  }

  private _findMarkerByTypeAndId(type: EPlotType, id: number): Marker {
    if (this._markersByType.has(type)) {
      const markersById = this._markersByType.get(type);
      if (markersById.has(id)) {
        return markersById.get(id);
      }
    }
    // not found
    return null;
  }

  private _createMarkersDiffer(): MapDiffer<MarkerInfo, number> {
    return new MapDiffer(
      item => item.id,
      (previousItem, newItem) =>
        previousItem.latLng.lat === newItem.latLng.lat &&
        previousItem.latLng.lng === newItem.latLng.lng &&
        previousItem.options.iconName === newItem.options.iconName &&
        // compare nullable properties requires checking for null
        ((isElementNull(previousItem.options.className) && isElementNull(newItem.options.className)) ||
          previousItem.options.className === newItem.options.className) &&
        ((isElementNull(previousItem.options.priority) && isElementNull(newItem.options.priority)) ||
          previousItem.options.priority === newItem.options.priority)
    );
  }

  private _createPlotsInBoundsDiffer(): MapDiffer<MarkerInfo, number> {
    return new MapDiffer(marker => marker.id);
  }

  private _getMarkersSize(): number {
    return Array.from(this._markersByType.values()).reduce((size, markers) => size + markers.size, 0);
  }

  private _getMarkersOfType(types: EAllPlotTypes[]): Marker[] {
    return types.reduce((markers: Marker[], type) => {
      const markersById = this._markersByType.get(type);
      if (isElementNull(markersById)) {
        return markers;
      }

      return markers.concat(Array.from(markersById.values()));
    }, []);
  }

  private _getMarkersInfoMapByType(markersInfo: MarkerInfo[]): Map<EPlotType, MarkerInfo[]> {
    const markersByType = new Map<EPlotType, MarkerInfo[]>();
    // initialize empty arrays of all types that are currently displayed
    this._markersDifferByType.forEach((_map, type) => {
      if (!markersByType.has(type)) {
        markersByType.set(type, []);
      }
    });

    // add markers to the map based on type
    markersInfo.forEach(marker => {
      const arr = markersByType.get(marker.type);
      if (isElementNull(arr)) {
        markersByType.set(marker.type, [marker]);
      } else {
        arr.push(marker);
      }
    });

    return markersByType;
  }

  private _getMarkersByIdMap(type: EPlotType): Map<number, Marker> {
    let markersMap = this._markersByType.get(type);
    if (isElementNull(markersMap)) {
      markersMap = new Map<number, Marker>();
      this._markersByType.set(type, markersMap);
    }
    return markersMap;
  }

  private _isClusteringEnabled(): boolean {
    return Array.from(this._plotClusterConfig.entries()).length > 0;
  }

  private _isClusteringEnabledForType(type: EPlotType): boolean {
    return this._isClusteringEnabled() && this._hasClusterer(type);
  }
  private _hasClusterer(type: EPlotType): boolean {
    return this._clusterersMap.has(type);
  }

  private _updateBounds(latLngs: ILatLng[], maxZoomLevelOveride?: number): void {
    const newBounds = new google.maps.LatLngBounds();

    latLngs.forEach(latLng => {
      newBounds.extend(latLng);
    });

    if (!this._mapBounds.equals(newBounds)) {
      this._setMapBoundsWithOffset(newBounds, maxZoomLevelOveride ?? this._maxZoomLevelForFitToMap);
    }
  }

  private _getClassNameFromMarker(marker: MarkerInfo): string {
    return [marker.options.className, marker.options.iconName].filter(className => !isStringNullOrEmpty(className)).join(' ');
  }

  private _shouldBringMarkerToForegroundOnHover(marker: Marker): boolean {
    return isElementNull(marker.markerInfo.options.priority) || marker.markerInfo.options.priority > 0;
  }

  private _processMarkersWhenReadyToProcess(): void {
    // when map is ready
    this.mapReady$
      .pipe(
        // switch to markers. map ready only fires once
        switchMapTo(
          this._markersToProcess$.pipe(
            // when we have markers to process, switch to processing, but cancel processing if newer markers arrive
            switchMap(markersToProcess =>
              // wait until ready to process
              this._readyToProcessMarkers$.pipe(
                take(1),
                map(() => markersToProcess),
                tap(markers => {
                  this._processMarkers(markers);
                })
              )
            )
          )
        )
      )
      .subscribe();
  }

  private _processMarkers(markers: MarkerInfo[]): void {
    // signal that markers are being processed
    this._isProcessingMarkersSubject.next(true);
    // process markers: observable fires once when work is completed
    this._setMapMarkers(markers)
      .pipe(
        take(1),
        // signal the work is completed
        tap(() => this._isProcessingMarkersSubject.next(false))
      )
      .subscribe();
  }

  private _getCenterAndZoomForMapInit(latLngs: LatLng[]) {
    const bounds = new google.maps.LatLngBounds();
    latLngs.forEach(l => {
      bounds.extend(l.toGoogleMaps());
    });

    const zoom = this._getZoomLevelForBounds(this._maxZoomLevelForFitToMap, bounds);
    const center = bounds.getCenter();
    // we cannot get the center that takes the offset into consideration because
    // the map projection is required and it is only available after the map is loaded
    // however, the difference in map center is small and the transition is mostly not noticable
    return { center, zoom };
  }

  private _updateMapDimensions(): void {
    const el = this._mapHostElement.nativeElement as HTMLElement;
    this._mapDimensions.height = el.offsetHeight;
    this._mapDimensions.width = el.offsetWidth;
    this._mapHostOffsetTop = el.getBoundingClientRect().top;
  }

  private _getClusterMarkerIdsByPlotType(cluster: Cluster): Map<EPlotType, number[]> {
    const typeIdMap = new Map<EPlotType, number[]>();
    const clusterMarkerIds: string[] = cluster.getMarkers().map(marker => (marker as unknown as CustomMarker).getId());
    clusterMarkerIds.forEach(markerId => {
      const delimitIndex = markerId.indexOf('_');
      const type = parseInt(markerId.substring(0, delimitIndex), 10) as EPlotType;
      const id = parseInt(markerId.substring(delimitIndex + 1), 10);

      typeIdMap.set(type, typeIdMap.has(type) ? [...typeIdMap.get(type), id] : [id]);
    });
    return typeIdMap;
  }

  private _onClusterRemoved(): void {
    // if "cluster balloon" is open
    if (this._balloon?.type === EPlotType.cluster) {
      this._closeBalloon(false);
    }

    // if a cluster is highlighed, remove the highlight
    if (this._positionHighlightOverlay?.marker.type === EPlotType.cluster) {
      this._removeHighlight();
    }
  }

  private _removeHighlight(): void {
    this._handleMarkerHighlight(null);
  }

  private _removeSelectedHighlight(): void {
    this._handleSelectedMarkerZIndex(null);
    this._handleSelectedMarkerHighlight(null);
  }

  private _handleClusterSingleClick(cluster: Cluster) {
    const clusterMarkerIdsByPlotType = this._getClusterMarkerIdsByPlotType(cluster);
    const clusterLatLng = cluster.getMarkers()[0].getPosition();
    const clusterClickEvent: IClusterClickEvent = {
      id: (cluster as any).clusterIcon_.get('id'),
      latLng: LatLng.fromGoogleMaps(clusterLatLng),
      markerIdsByPlotType: clusterMarkerIdsByPlotType
    };
    this._highlightSelectedCluster(cluster);
    this.clusterClicked.emit(clusterClickEvent);
  }

  private _clusterZoomTo(cluster: Cluster) {
    const clusterLatLngs = cluster.getMarkers().map(marker => ({
      lat: marker.getPosition().lat(),
      lng: marker.getPosition().lng()
    }));
    this._updateBounds(clusterLatLngs, this._clusterZoomToMaxZoomLevel);
    event.preventDefault();
  }

  private _isClusterDoubleClick() {
    const doubleClickDebouceMs = 500;
    const previousClusterClick = this._clusterClickTime;
    this._clusterClickTime = new Date();
    const isDoubleClick = previousClusterClick && this._clusterClickTime.getTime() - previousClusterClick.getTime() < doubleClickDebouceMs;
    return isDoubleClick;
  }

  private setSelectedMarker(marker: MarkerInfo): void {
    this._positionHighlightOverlayInitialized$.pipe(take(1)).subscribe(() => {
      this._handleSelectedMarkerZIndex(marker);
      this._handleSelectedMarkerHighlight(marker);
    });
  }

  private setZoomToMarker(location: ILatLng): void {
    if (!isElementNull(location)) {
      this._mapStable$.subscribe(() => this._setZoomToMarker(location));
    }
  }
}
